/**
 * DefectParetoChart — pack widget showing defects sorted by frequency
 * with cumulative-% line overlay (the canonical Pareto chart).
 *
 * Source: ported from
 *   D:\WORK\SAHAS\Projects\Digital Twin Concept\app\src\components\analytics\AnalyticsView.tsx
 */

import { useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/ui/cn";

export interface DefectCount {
  name: string;
  count: number;
}

export interface DefectParetoChartProps {
  defects: DefectCount[];
  height?: number;
  className?: string;
}

export function DefectParetoChart({ defects, height = 320, className }: DefectParetoChartProps) {
  const data = useMemo(() => {
    const sorted = [...defects].sort((a, b) => b.count - a.count);
    const total = sorted.reduce((s, d) => s + d.count, 0) || 1;
    let cum = 0;
    return sorted.map((d) => {
      cum += d.count;
      return {
        name: d.name.replace(/_/g, " "),
        count: d.count,
        cumPct: Math.round((cum / total) * 100),
      };
    });
  }, [defects]);

  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 12, right: 24, bottom: 28, left: 12 }}>
          <CartesianGrid stroke="rgb(var(--color-border))" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="name"
            stroke="rgb(var(--color-on-surface-muted))"
            tick={{ fontSize: 10 }}
            angle={-20}
            textAnchor="end"
            interval={0}
            height={48}
          />
          <YAxis
            yAxisId="left"
            stroke="rgb(var(--color-on-surface-muted))"
            tick={{ fontSize: 11 }}
            label={{
              value: "Count",
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 11, fill: "rgb(var(--color-on-surface-muted))", textAnchor: "middle" },
            }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, 100]}
            stroke="rgb(var(--color-on-surface-muted))"
            tick={{ fontSize: 11 }}
            label={{
              value: "Cumulative %",
              angle: 90,
              position: "insideRight",
              style: { fontSize: 11, fill: "rgb(var(--color-on-surface-muted))", textAnchor: "middle" },
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "rgb(var(--color-surface-elevated))",
              border: "1px solid rgb(var(--color-border))",
              borderRadius: 6,
              fontSize: 12,
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar
            yAxisId="left"
            dataKey="count"
            fill="rgb(var(--color-danger))"
            name="Defects"
            radius={[3, 3, 0, 0]}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="cumPct"
            stroke="rgb(var(--color-warning))"
            strokeWidth={2}
            dot={{ r: 3 }}
            name="Cumulative %"
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
